import _ from 'underscore';
import { getDefaultActionForGoalCategory } from './conversion-goals-repository';
import { STEP_PATHS } from './step-paths';

export const basicSteps = [
  STEP_PATHS.goalObjective,
  STEP_PATHS.goalType,
  STEP_PATHS.goalDetails.normalGoal,
  STEP_PATHS.tagSetup.noTag.selectTagMethod,
  STEP_PATHS.endFlow,
];

function goToNextStep(state, currentStep) {
  const stepRoot = currentStep.split('.').shift();
  const stepIndex = _.findIndex(basicSteps, step => step.id.split('.').shift() === stepRoot);

  return {
    ...state,
    steps: [...state.steps, currentStep],
    currentStep: basicSteps[stepIndex + 1].id,
    loggingSteps: [...state.steps, currentStep],
  };
}

function goToPreviousStep(state) {
  return {
    ...state,
    steps: state.steps.slice(0, -1),
    currentStep: state.steps[state.steps.length - 1],
    loggingSteps: [...state.steps, state.currentStep],
  };
}

function goToStep(state, currentStep, nextStep) {
  return {
    ...state,
    currentStep: nextStep,
    steps: [...state.steps, currentStep],
    loggingSteps: [...state.steps, currentStep],
  };
}

export const WIZARD_ACTIONS = {
  WIZARD_NAVIGATE_BACK: 'WIZARD_NAVIGATE_BACK',
  WIZARD_NAVIGATE_NEXT: 'WIZARD_NAVIGATE_NEXT',
  SELECT_OBJECTIVE: 'SELECT_OBJECTIVE',
  SAVE_AND_NAVIGATE_DETAILS: 'SAVE_AND_NAVIGATE_DETAILS',
  WIZARD_SAVE_AND_NAVIGATE_TAG_SELECTION: 'WIZARD_SAVE_AND_NAVIGATE_TAG_SELECTION',
  NAVIGATE_WITH_TAGINSTALL_OPTIONS: 'NAVIGATE_WITH_TAGINSTALL_OPTIONS',
  SELECT_TAG_SETUP_METHOD: 'SELECT_TAG_SETUP_METHOD',
  EVENT_TAG_SETUP: 'EVENT_TAG_SETUP',
  NAVIGATE_WITH_TAG_INSTALL_INSTRUCTIONS: 'NAVIGATE_WITH_TAG_INSTALL_INSTRUCTIONS',
  GO_TO_EMAIL_DEVELOPER_STEP: 'GO_TO_EMAIL_DEVELOPER_STEP',
  NAVIGATE_WITH_PLATFORM: 'NAVIGATE_WITH_PLATFORM',
  WIZARD_UPDATE_GTM_SESSION_CONTEXT: 'WIZARD_UPDATE_GTM_SESSION_CONTEXT',
  UPDATE_ASSOCIATED_CAMPAIGNS_DATA: 'UPDATE_ASSOCIATED_CAMPAIGNS_DATA',
  NAVIGATE_TO_LAST_STEP: 'NAVIGATE_TO_LAST_STEP',
  NAVIGATE_TO_ENHANCED_CONV: 'NAVIGATE_TO_ENHANCED_CONV',
  NAVIGATE_TO_SETUP_ENHANCED_CONV: 'NAVIGATE_TO_SETUP_ENHANCED_CONV',
};


const getDefaultCommonSettings = (state, goalType) => ({
  CurrencyCode: 'USD',
  GoalValue: state.permissions && state.permissions.dynamic && state.permissions.IsAmplifyEnabled && state.permissions.dynamic.BoostLTV ? 0.011 : 1,
  IsAccountLevel: !(goalType === 'InStoreVisitGoal' || goalType === 'InStoreTransactionGoal' || goalType === 'ApplicationInstallGoal'),
  ConversionCountType: goalType === 'InStoreVisitGoal' ? 'Unique' : 'All',
  IsMainConversionGoal: goalType !== 'InStoreVisitGoal',
  LookbackWindowInMinutes: goalType === 'InStoreVisitGoal' ? 1440 : 43200,
  ViewThroughLookbackWindowinMinutes: 1440,
});

const getDefaultSpecificSettings = (goalType, goalCategory) => {
  switch (goalType) {
    case 'PageViewsPerVisitGoal':
      return {
        Operator: 'GreaterThan',
        PageViews: 0,
      };
    case 'DurationGoal':
      return {
        Operator: 'GreaterThan',
        Duration: 0,
      };
    case 'ApplicationInstallGoal':
      return {
        ApplicationPlatform: 'Windows Phone',
        ApplicationStoreId: '',
      };
    case 'DestinationGoal':
      return {
        Operator: null,
        UrlString: '',
      };
    case 'EventGoal':
      return {
        CategoryOperator: 'EqualsTo',
        Category: null,
        ActionOperator: 'EqualsTo',
        Action: getDefaultActionForGoalCategory(goalCategory),
        LabelOperator: 'EqualsTo',
        Label: null,
        ValueOperator: 'EqualTo',
        Value: null,
      };
    default:
      return {};
  }
};

const allowEventTagSetupWithClarity = (state, goalType) => {
  const clarityTagSetupSupportedGoalTypes = ['EventGoal'];
  return state.permissions &&
    state.permissions.dynamic && state.permissions.dynamic.IsClarityTagSetupAllowed
    && clarityTagSetupSupportedGoalTypes.includes(goalType);
};

export const goalCreationWizardReducer = (state, action) => {
  switch (action.type) {
    case WIZARD_ACTIONS.WIZARD_NAVIGATE_NEXT:
      return goToNextStep(state, state.currentStep);
    case WIZARD_ACTIONS.WIZARD_NAVIGATE_BACK:
      return goToPreviousStep(state);
    case WIZARD_ACTIONS.SELECT_TAG_SETUP_METHOD:
      return goToStep({
        ...state,
        ...action.data,
      }, state.currentStep, STEP_PATHS.tagSetup.noTag[action.data.selectedTagSetupMethod].id);
    case WIZARD_ACTIONS.EVENT_TAG_SETUP: {
      const goToClarityEventTagSetup = action.eventTagSetupMethod === 'clarity';
      return goToStep({
        ...state,
        goalObject: {
          ...state.goalObject,
          ...action.data,
        },
      }, state.currentStep, goToClarityEventTagSetup ?
        STEP_PATHS.tagSetup.addEventTagWithClarity.id : STEP_PATHS.tagSetup.addEventTag.id);
    }
    case WIZARD_ACTIONS.GO_TO_EMAIL_DEVELOPER_STEP:
      return goToStep(state, state.currentStep, STEP_PATHS.tagSetup.noTag.emailTag.id);
    case WIZARD_ACTIONS.NAVIGATE_WITH_PLATFORM:
      return goToStep({
        ...state,
        ...action.data,
      }, state.currentStep, STEP_PATHS.tagSetup.noTag.platformTagInstructions.id);
    case WIZARD_ACTIONS.SELECT_OBJECTIVE:
      return {
        ...state,
        selectedObjective: action.value,
      };
    case WIZARD_ACTIONS.SAVE_AND_NAVIGATE_DETAILS: {
      const defaultCommonSettings = getDefaultCommonSettings(state, action.data.Type);
      const defaultSpecificSettings = getDefaultSpecificSettings(action.data.Type, action.data.GoalCategory);
      const reservedProps = _.isUndefined(state.goalObject) || !state.goalObject.Id ? {
        Status: 'Active',
      } : {
        Id: state.goalObject.Id,
        Status: state.goalObject.Status,
        Name: state.goalObject.Name,
        IsAccountLevel: state.goalObject.IsAccountLevel,
      };
      const goalTypeChanged = !_.isUndefined(state.goalObject) && state.goalObject.Type !== action.data.Type;
      // reset goalObject on goal type change
      let updatedStates = _.isUndefined(state.goalObject) || goalTypeChanged
        || (!state.isEditScenario && state.goalObject.GoalCategory !== action.data.GoalCategory) ? {
          ...state,
          goalObject: {
            ...action.data,
            ...defaultCommonSettings,
            ...defaultSpecificSettings,
            ...reservedProps,
          },
        } : {
          ...state,
          goalObject: {
            ...state.goalObject,
            ...action.data,
          },
        };

      if (goalTypeChanged && state.goalObject.ClarityEventDefinitionId) {
        updatedStates = {
          ...updatedStates,
          deleteClarityEvent: true,
        };
      }

      // Reset LinkedIn settings if the goal type is changed
      if (goalTypeChanged) {
        updatedStates = {
          ...updatedStates,

          goalObject: {
            ...updatedStates.goalObject,
            SelectedNetwork: undefined,
          },

          associatedCampaigns: {
            ...state.associatedCampaigns,
            current: [],
          },
        };
      }

      return goToNextStep(updatedStates, state.currentStep);
    }
    case WIZARD_ACTIONS.WIZARD_SAVE_AND_NAVIGATE_TAG_SELECTION: {
      const updatedState = {
        ...state,
        goalObject: {
          ...state.goalObject,
          ...action.data,
        },
      };
      return goToStep(updatedState, state.currentStep, STEP_PATHS.tagSetup.selectTag.id);
    }
    case WIZARD_ACTIONS.NAVIGATE_WITH_TAG_INSTALL_INSTRUCTIONS: {
      return goToStep({
        ...state,
        ...action.data,
      }, state.currentStep, allowEventTagSetupWithClarity(state, state.goalObject.Type) ?
        STEP_PATHS.tagSetup.eventTagChoice.id : STEP_PATHS.tagSetup.addEventTag.id);
    }
    case WIZARD_ACTIONS.NAVIGATE_WITH_TAGINSTALL_OPTIONS: {
      const updatedState = {
        ...state,
        goalObject: {
          ...state.goalObject,
          ...action.data, // update selected tag id
        },
        tagInstallOption: action.tagInstallOption,
        isSaveDone: true,
        isEditScenario: state.isEditScenario || action.switchToEdit,
        goToSetUpEnhancedConversions: action.goToSetUpEnhancedConversions,
      };

      const { goToSetUpEnhancedConversions = false } = action;
      const showEventTagSetup = updatedState.goalObject.Type === 'EventGoal'
        || updatedState.goalObject.Type === 'ProductConversionGoal'
        || updatedState.goalObject.GoalValueType === 'VariantValue';

      if (action.tagInstallOption !== 'yes') {
        return goToStep(updatedState, state.currentStep, STEP_PATHS.tagSetup.noTag.selectTagMethod.id);
      }

      let nextStepIfTagInstalled = null;
      if (showEventTagSetup && allowEventTagSetupWithClarity(state, updatedState.goalObject.Type)) {
        if (goToSetUpEnhancedConversions) {
          nextStepIfTagInstalled = STEP_PATHS.tagSetup.setupEnhancedConv.id;
        } else {
          nextStepIfTagInstalled = STEP_PATHS.tagSetup.eventTagChoice.id;
        }
      } else if (showEventTagSetup) {
        if (goToSetUpEnhancedConversions) {
          nextStepIfTagInstalled = STEP_PATHS.tagSetup.setupEnhancedConv.id;
        } else {
          nextStepIfTagInstalled = STEP_PATHS.tagSetup.addEventTag.id;
        }
      } else if (goToSetUpEnhancedConversions) {
        nextStepIfTagInstalled = STEP_PATHS.tagSetup.setupEnhancedConv.id;
      } else {
        nextStepIfTagInstalled = STEP_PATHS.endFlow.id;
      }

      return goToStep(updatedState, state.currentStep, nextStepIfTagInstalled);
    }
    case WIZARD_ACTIONS.NAVIGATE_TO_LAST_STEP: {
      const updatedState = {
        ...state,
        goalObject: {
          ...state.goalObject,
          ...action.data,
        },
        isSaveDone: true,
        isEditScenario: state.isEditScenario || action.switchToEdit,
      };
      return goToStep(updatedState, state.currentStep, STEP_PATHS.endFlow.id);
    }
    case WIZARD_ACTIONS.NAVIGATE_TO_ENHANCED_CONV: {
      const updatedState = {
        ...state,
        goalObject: {
          ...state.goalObject,
          ...action.data,
        },
        isEditScenario: state.isEditScenario || action.switchToEdit,
      };
      return goToStep(updatedState, state.currentStep, STEP_PATHS.goalDetails.enhancedConversions.id);
    }
    case WIZARD_ACTIONS.NAVIGATE_TO_SETUP_ENHANCED_CONV: {
      const updatedState = {
        ...state,
        goalObject: {
          ...state.goalObject,
          ...action.data,
        },
      };
      return goToStep(updatedState, state.currentStep, STEP_PATHS.tagSetup.setupEnhancedConv.id);
    }
    case WIZARD_ACTIONS.WIZARD_UPDATE_GTM_SESSION_CONTEXT: {
      return {
        ...state,
        GTMContext: { ...action.GTMContext },
      };
    }
    case WIZARD_ACTIONS.UPDATE_ASSOCIATED_CAMPAIGNS_DATA: {
      return {
        ...state,
        associatedCampaigns: {
          ...state.associatedCampaigns,
          ...action.associatedCampaigns,
        },
      };
    }
    default:
      throw new Error('invalid goal creation wizard action');
  }
};

